import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  #paypalRetryCount = 0;
  #paypalRetryTimer;

  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    this.#renderPaypalMessage();
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    if (this.#paypalRetryTimer) clearTimeout(this.#paypalRetryTimer);
  }

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const matchingProductPrice = this.#findMatchingProductPrice(event.detail.data.html);
    const newPrice = matchingProductPrice?.querySelector('[ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');
    const newPaypalAmount = matchingProductPrice?.dataset.paypalAmount;

    if (!newPrice || !currentPrice) return;

    if (currentPrice.innerHTML !== newPrice.innerHTML) {
      currentPrice.replaceWith(newPrice);
    }

    if (newPaypalAmount && newPaypalAmount !== this.dataset.paypalAmount) {
      this.dataset.paypalAmount = newPaypalAmount;
      this.#paypalRetryCount = 0;
      this.#renderPaypalMessage();
    }
  };

  #renderPaypalMessage() {
    const container = this.querySelector('[ref="paypalMessage"]');
    const amount = Number(this.dataset.paypalAmount) / 100;
    const paypal = window.paypal;

    if (!container?.id || !Number.isFinite(amount)) return;

    if (!paypal?.Messages) {
      if (this.#paypalRetryCount < 20) {
        this.#paypalRetryCount += 1;
        this.#paypalRetryTimer = setTimeout(() => this.#renderPaypalMessage(), 500);
      }
      return;
    }

    container.replaceChildren();

    try {
      const renderResult = paypal.Messages({
        amount,
        placement: 'product',
        style: {
          layout: 'text',
          text: {
            color: 'white',
          },
        },
      }).render(`#${container.id}`);

      renderResult?.catch?.(() => {});
    } catch {
      // Keep the theme usable when PayPal rejects a message configuration.
    }
  }

  /**
   * Finds the matching product-price element in fetched HTML.
   * @param {Document} html - The parsed HTML document from the variant update response.
   * @returns {Element | null}
   */
  #findMatchingProductPrice(html) {
    const productId = this.dataset.productId;
    if (!productId) return html.querySelector('product-price');

    const productPrices = html.querySelectorAll(`product-price[data-product-id="${productId}"]`);
    if (productPrices.length <= 1) return productPrices[0] ?? html.querySelector('product-price');

    const showDiscount = this.dataset.showDiscount ?? '';
    for (const productPrice of productPrices) {
      if ((productPrice.dataset.showDiscount ?? '') === showDiscount) {
        return productPrice;
      }
    }

    return productPrices[0];
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
